import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint } from "@fortawesome/free-solid-svg-icons";
import styles from "./RaffleTermsPage.module.css";
import logoLight from "../../../assets/logos/Logo_Gold_black.png";
import logoDark from "../../../assets/logos/Logo_Gold.png";
import useDocumentMeta from "../blog/useDocumentMeta";
import { sanitizeBlogHtml } from "../blog/blogUtils";
import { Spinner } from "../../../components/ui";
import { raffleService } from "../../../services/api";
import { formatPromoDateTime, formatPromoDateTimeInline } from "./promoUtils";

/**
 * T&C permanentes de una rifa: /terminos-rifa/:slug?version=N (pública, sin login).
 * Es el enlace que acompaña al comprobante: debe funcionar aunque la rifa termine,
 * se cancele o el módulo esté apagado.
 */
const RaffleTermsPage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const rawVersion = searchParams.get("version");
  const version = rawVersion && /^\d+$/.test(rawVersion) ? rawVersion : undefined;
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    raffleService.getTerms(slug, version).then((res) => {
      if (cancelled) return;
      setTerms(res.ok ? res.data : null);
      setStatus(res.status);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [slug, version]);

  const html = useMemo(() => sanitizeBlogHtml(terms?.terms_html), [terms?.terms_html]);
  const versions = Array.isArray(terms?.versions) ? terms.versions : [];
  const basePath = `/terminos-rifa/${encodeURIComponent(slug || "")}`;

  useDocumentMeta(
    terms ? `Términos y condiciones · ${terms.title} | Gold Loterías` : "Términos y condiciones | Gold Loterías",
    terms ? `Términos y condiciones de ${terms.title} en Gold Loterías.` : ""
  );

  return (
    <div className={styles.page}>
      <header className={styles.brandBar}>
        <Link to="/welcome" className={styles.logoLink}>
          {/* Dos variantes: el CSS muestra la del tema y, al imprimir, siempre la oscura. */}
          <img src={logoLight} alt="Gold Loterías" className={`${styles.logo} ${styles.logoOnLight}`} />
          <img src={logoDark} alt="" aria-hidden="true" className={`${styles.logo} ${styles.logoOnDark}`} />
        </Link>
        {terms && (
          <button type="button" className={styles.printBtn} onClick={() => window.print()}>
            <FontAwesomeIcon icon={faPrint} /> Imprimir
          </button>
        )}
      </header>

      <main className={styles.sheet}>
        {loading && <Spinner label="Cargando términos y condiciones…" />}

        {!loading && !terms && (
          <div className={styles.empty}>
            <h1 className={styles.title}>
              {status === 404
                ? "No encontramos estos términos y condiciones"
                : "No pudimos cargar los términos y condiciones"}
            </h1>
            <p className={styles.meta}>
              {status === 404
                ? "Revisa que el enlace esté completo."
                : "Revisa tu conexión e inténtalo de nuevo en unos minutos."}
            </p>
          </div>
        )}

        {!loading && terms && (
          <>
            <p className={styles.eyebrow}>Términos y condiciones</p>
            <h1 className={styles.title}>{terms.title}</h1>
            <p className={styles.meta}>
              Versión {terms.version}
              {terms.effective_from
                ? ` · Vigente desde el ${formatPromoDateTimeInline(terms.effective_from)}`
                : ""}
            </p>

            {terms.is_current === false && (
              <p className={styles.notice}>
                Esta es una versión anterior (la que aceptaste al comprar).{" "}
                {terms.current_version ? (
                  <Link to={`${basePath}?version=${terms.current_version}`}>
                    Ver la versión vigente
                  </Link>
                ) : null}
              </p>
            )}

            <div
              className={styles.body}
              // terms_html viene saneado del backend y se limpia de nuevo en sanitizeBlogHtml.
              dangerouslySetInnerHTML={{ __html: html }}
            />

            {versions.length > 1 && (
              <nav className={styles.versions} aria-label="Versiones">
                <p className={styles.versionsTitle}>Otras versiones</p>
                <ul>
                  {versions.map((v) => (
                    <li key={v.version}>
                      {String(v.version) === String(terms.version) ? (
                        <span className={styles.currentVersion}>
                          Versión {v.version}
                          {v.effective_from ? ` · ${formatPromoDateTime(v.effective_from)}` : ""}
                        </span>
                      ) : (
                        <Link to={`${basePath}?version=${v.version}`}>
                          Versión {v.version}
                          {v.effective_from ? ` · ${formatPromoDateTime(v.effective_from)}` : ""}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </nav>
            )}
          </>
        )}
      </main>

      <footer className={styles.footer}>
        © {new Date().getFullYear()} Gold Loterías · goldloterias.com
      </footer>
    </div>
  );
};

export default RaffleTermsPage;
